import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getCookie, setCookie } from '@/lib/cookies';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeCtx = createContext<ThemeContextValue | null>(null);

function applyTheme(t: Theme) {
  const html = document.documentElement;
  html.classList.toggle('theme-dark', t === 'dark');
  html.classList.toggle('theme-light', t === 'light');
  html.setAttribute('data-theme', t);
}

function detectInitial(): Theme {
  const c = getCookie('kispro-theme');
  if (c === 'dark' || c === 'light') return c;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => detectInitial());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    setCookie('kispro-theme', t, { days: 365, crossDomain: true });
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      setCookie('kispro-theme', next, { days: 365, crossDomain: true });
      return next;
    });
  }, []);

  return <ThemeCtx.Provider value={{ theme, setTheme, toggle }}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
